'use client';

import { Calendar } from '@eliadmin/core/src/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@eliadmin/core/src/components/ui/popover';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Input } from '@eliadmin/core/src/components/ui/input';
import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@eliadmin/core/src/components/ui/form';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useFormContext } from 'react-hook-form';
import type {
  ReservationFormInputValues,
  ReservationFormValues,
} from '@eliadmin/core/src/components/reservation/reservation-form/reservation-form-schema';
import { format } from 'date-fns';
import { LuMinus, LuPlus } from 'react-icons/lu';
import { useState } from 'react';
import { FaRegCalendarAlt } from 'react-icons/fa';

import { AiOutlineExclamationCircle } from 'react-icons/ai';

const calendarClassNames = {
  day_button: 'h-8 w-8 p-0 font-normal text-foreground rounded-md hover:bg-muted cursor-pointer',
  selected:
    'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground rounded-md',
  outside: 'text-muted-foreground opacity-40',
  disabled: 'text-muted-foreground opacity-30 cursor-not-allowed pointer-events-none',
  today: 'bg-muted font-semibold',
  weekday: 'text-muted-foreground w-8 font-medium text-[0.75rem] text-center',
};

interface DatePickerProps {
  value: Date | undefined;
  placeholder: string;
  hasError: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onChange: (date: Date | undefined) => void;
  disabled?: (date: Date) => boolean;
}

function DatePickerField({
  value,
  placeholder,
  hasError,
  open,
  onOpenChange,
  onChange,
  disabled,
}: DatePickerProps) {
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild className="w-full">
        <Button
          type="button"
          variant={'none'}
          className={cn(
            'h-[58px] w-full justify-between gap-3 border px-6 text-left font-normal',
            !value && 'text-[#9E9E9E]',
            hasError && 'border-destructive',
          )}
        >
          {value ? format(value, 'd. M. yyyy') : placeholder}
          <FaRegCalendarAlt className="h-5 w-5 flex-shrink-0 text-secondary" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={value}
          onSelect={date => {
            onChange(date);
            onOpenChange(false);
          }}
          classNames={calendarClassNames}
          disabled={disabled}
        />
      </PopoverContent>
    </Popover>
  );
}

function isPeakSeason(date: Date): boolean {
  const month = date.getMonth(); // 0-indexed
  const day = date.getDate();
  const isJuly = month === 6;
  const isAugust = month === 7;
  const isChristmas = (month === 11 && day >= 24) || (month === 0 && day <= 3);
  return isJuly || isAugust || isChristmas;
}

function addNights(date: Date, nights: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + nights);
  return result;
}

export function ReservationFormStep1() {
  const form = useFormContext<ReservationFormInputValues, any, ReservationFormValues>();
  const [arrivalOpen, setArrivalOpen] = useState(false);
  const [departureOpen, setDepartureOpen] = useState(false);

  const arrivalRaw = form.watch('arrival');
  const arrival = (arrivalRaw as any) instanceof Date ? (arrivalRaw as Date) : undefined;
  const peakSeason = arrival ? isPeakSeason(arrival) : false;
  const minNights = peakSeason ? 3 : 2;

  return (
    <div className="grid w-full grid-cols-12 gap-4 sm:gap-6">
      <FormField
        control={form.control}
        name="arrival"
        render={({ field }) => {
          const value = (field.value as any) instanceof Date ? (field.value as Date) : undefined;
          return (
            <FormItem className="col-span-12 sm:col-span-6">
              <FormControl>
                <DatePickerField
                  value={value}
                  placeholder="Príchod *"
                  hasError={!!form.formState.errors.arrival}
                  open={arrivalOpen}
                  onOpenChange={setArrivalOpen}
                  onChange={field.onChange}
                  disabled={date => date < new Date(new Date().setHours(0, 0, 0, 0))}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          );
        }}
      />

      <FormField
        control={form.control}
        name="departure"
        render={({ field }) => {
          const value = (field.value as any) instanceof Date ? (field.value as Date) : undefined;
          return (
            <FormItem className="col-span-12 sm:col-span-6">
              <FormControl>
                <DatePickerField
                  value={value}
                  placeholder="Odchod *"
                  hasError={!!form.formState.errors.departure}
                  open={departureOpen}
                  onOpenChange={setDepartureOpen}
                  onChange={field.onChange}
                  disabled={date => {
                    const today = new Date(new Date().setHours(0, 0, 0, 0));
                    return arrival ? date < addNights(arrival, minNights) : date < today;
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          );
        }}
      />

      <FormField
        control={form.control}
        name="guests_count"
        render={({ field }) => {
          const value = Number(field.value) || 2;
          return (
            <FormItem className="col-span-12 w-full rounded-full border bg-white">
              <FormControl>
                <Input
                  {...(field as any)}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  placeholder="Počet hostí *"
                  textAlign={'center'}
                  className={{
                    wrapper: 'border',
                    before: 'pl-3',
                    after: 'pr-3',
                  }}
                  before={
                    <button
                      type="button"
                      onClick={() => field.onChange(Math.max(2, value - 1))}
                      disabled={value <= 2}
                      className="flex h-[32px] w-[32px] flex-shrink-0 items-center justify-center rounded-full bg-primary text-black transition-colors hover:text-foreground disabled:opacity-30"
                    >
                      <LuMinus className="h-4 w-4" />
                    </button>
                  }
                  after={
                    <button
                      type="button"
                      onClick={() => field.onChange(value + 1)}
                      className="flex h-[32px] w-[32px] flex-shrink-0 items-center justify-center rounded-full bg-primary text-black transition-colors hover:text-foreground"
                    >
                      <LuPlus className="h-4 w-4" />
                    </button>
                  }
                  onChange={e => {
                    const parsed = parseInt(e.target.value, 10);
                    field.onChange(isNaN(parsed) ? '' : Math.max(1, parsed));
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          );
        }}
      />
      {peakSeason && (
        <div className="col-span-12 flex w-full items-center justify-start gap-2 rounded-[24px] bg-[#CBEDFF] px-4 py-4 sm:px-6 sm:py-3.5 md:rounded-full">
          <AiOutlineExclamationCircle className="max-h-6 min-h-6 min-w-6 max-w-6 text-[#133274]" />
          <p className="w-full text-left text-sm font-bold leading-[130%] tracking-tight text-[#133274] sm:text-base sm:leading-[130%]">
            Minimálny pobyt je 2 noci. V hlavnej sezóne (júl, august, vianoce) 3 noci.
          </p>
        </div>
      )}
    </div>
  );
}
